import Component from '@glimmer/component';
import { service } from '@ember/service';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import { PhDatabase, PhFlask } from 'ember-phosphor-icons';
import Chip from 'tio-ui/components/chip';

export type DataSource = 'live' | 'demo' | 'placeholder';

interface DataIndicatorSignature {
  Args: {
    /**
     * The data source for this section
     * - 'live': Real data from the API
     * - 'demo': Demo/mock data
     * - 'placeholder': Section not yet hooked up to real data
     */
    source: DataSource;
    /**
     * Whether to show the marching ants border for placeholder data
     * @default true
     */
    showBorder?: boolean;
    /**
     * Whether to show the indicator badge
     * @default true
     */
    showBadge?: boolean;
    /**
     * Custom label for the badge
     */
    label?: string;
    /**
     * ID that links to the DATA_SOURCES.md documentation.
     * When clicked in dev mode, opens the doc panel and scrolls to this section.
     * Should match the markdown header ID (lowercase, hyphenated).
     * @example "student-loans", "section-127", "tas-eligibility"
     */
    dataSourceId?: string;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

/**
 * DataIndicator Component
 *
 * Wraps a section and shows visual indicators for data source:
 * - In demo mode: All sections show as "Demo Data" (blue badge)
 * - In live mode with live data: Shows "Live Data" (green badge)
 * - In live mode with placeholder: Shows marching ants border + "Not Connected" (orange badge)
 *
 * The marching ants and badges are ONLY shown when NOT in demo mode,
 * to help developers identify which sections need live data integration.
 *
 * Usage:
 * ```gts
 * <DataIndicator @source="live">
 *   <MyContent />
 * </DataIndicator>
 * ```
 */
export default class DataIndicator extends Component<DataIndicatorSignature> {
  @service declare reimaginedData: ReimaginedDataService;

  get showBorder() {
    return this.args.showBorder ?? true;
  }

  get showBadge() {
    return this.args.showBadge ?? true;
  }

  /**
   * In demo mode, treat everything as demo regardless of source
   */
  get effectiveSource(): DataSource {
    if (this.reimaginedData.isDemoMode) {
      return 'demo';
    }
    return this.args.source;
  }

  get isPlaceholder() {
    return this.effectiveSource === 'placeholder';
  }

  get isDemo() {
    return this.effectiveSource === 'demo';
  }

  get isLive() {
    return this.effectiveSource === 'live';
  }

  /**
   * Show indicators based on the service-level toggle.
   * Can be controlled via URL param ?show_badges=true/false
   * or defaults to showing in live mode only.
   * Hidden when docs drawer is open to avoid z-index overlap.
   */
  get shouldShowIndicators() {
    return this.reimaginedData.showDataSourceBadges && !this.reimaginedData.isDocsOpen;
  }

  get containerClass() {
    const classes = ['relative'];

    // Show marching ants animation for placeholder sections in live mode
    if (this.isPlaceholder && this.showBorder && this.shouldShowIndicators) {
      classes.push('marching-ants-container');
    }

    return classes.join(' ');
  }

  get badgeIntent(): 'success' | 'primary' | 'warning' {
    if (this.isLive) return 'success';
    if (this.isDemo) return 'primary';
    return 'warning';
  }

  get badgeLabel() {
    if (this.args.label) return this.args.label;
    if (this.isLive) return 'Live Data';
    if (this.isDemo) return 'Demo Data';
    return 'Not Connected';
  }

  get tooltipText() {
    if (this.isLive)
      return 'This section displays real data from your account - Click to view docs';
    if (this.isDemo) return 'This section displays sample demonstration data - Click to view docs';
    return 'This section is not yet connected to live data - Click to view docs';
  }

  get Icon() {
    if (this.isLive || this.isDemo) return PhDatabase;
    return PhFlask;
  }

  /**
   * Opens the data source documentation panel and scrolls to the relevant section
   */
  openDataSourceDoc = () => {
    this.reimaginedData.openDocs(this.args.dataSourceId);
  };

  /**
   * Container class includes data-audit for CSS marching ants effect
   * with source-specific colors (green for live, blue for demo, red for placeholder)
   */
  get containerClasses() {
    const classes = ['relative'];

    // Add data-audit classes for CSS marching ants when indicators are enabled
    if (this.showBorder && this.shouldShowIndicators) {
      classes.push('data-audit');
      if (this.isLive) {
        classes.push('data-audit-live');
      } else if (this.isDemo) {
        classes.push('data-audit-demo');
      } else if (this.isPlaceholder) {
        classes.push('data-audit-placeholder');
      }
    }

    return classes.join(' ');
  }

  <template>
    <div class={{this.containerClasses}} ...attributes>
      {{#if this.shouldShowIndicators}}
        {{#if this.showBadge}}
          <button
            type="button"
            class="absolute -top-3 -right-3 z-[5]"
            title={{this.tooltipText}}
            {{on "click" this.openDataSourceDoc}}
          >
            <Chip
              @intent={{this.badgeIntent}}
              class="text-xs gap-1 shadow-md cursor-pointer hover:scale-105 transition-transform"
            >
              <this.Icon @size={{12}} />
              {{this.badgeLabel}}
            </Chip>
          </button>
        {{/if}}
      {{/if}}
      {{yield}}
    </div>
  </template>
}
